/** @jsxImportSource preact */
import type { SupportRequestFields } from "@go-novus/admin-api";
import { REQUEST_TYPE_LABELS } from "./contact-card";
import { requestTypes } from "./request-type";
import type { VNode } from "preact";

import * as m from "../../paraglide/messages.js";
import { CARD_STYLES } from "../form/card-styles";
import { FormButton } from "../form/form-button";
import { FORM_STYLES, formVars } from "../form/form-styles";
import {
  BanIcon,
  CheckIcon,
  CircleHelpIcon,
  CircleXIcon,
  PaperPlaneIcon,
  RefreshIcon,
  SpinnerIcon,
} from "../icons";
import type { WidgetTokens } from "../theme";
import {
  CARD_PARTS_STYLES,
  FactRow,
  Facts,
  hasStoreContact,
  StatusHeader,
  StoreContactList,
  type WidgetIconTone,
  type WidgetStoreContact,
} from "./card-parts";

/** 请求邮件走到哪一步了。同一张卡按状态原地更新。 */
export type WidgetEmailStatus =
  "accepted" | "bounced" | "delivered" | "failed" | "sending" | "unknown";

export interface WidgetEmailResult {
  /** 买家填的联系邮箱，商家回复发到这里。 */
  contactEmail: string;
  requestType?: SupportRequestFields["request_type"];
  acceptedAt?: string;
  /** 只在退信时给：邮件没送到，列出其他联系商家的办法。 */
  contact?: WidgetStoreContact;
  /** 请求编号，如 `GS-0001`，原文由后端给。 */
  requestId: string;
  status: WidgetEmailStatus;
  /** 时间已按买家语言格式化。发送中和已接受时标 Submitted，其余标 Updated。 */
  time?: string;
  /** 收件的商家邮箱。 */
  to: string;
}

export interface EmailResultCardProps {
  /** 有了它，非发送中、非失败的状态多出「Start a new request」；同一个问题再次求助时用。 */
  onStartNew?: () => void;
  onEdit?: () => void;
  onCheck?: () => void;
  /** 发送失败时的「Retry」。 */
  onRetry?: () => void;
  result: WidgetEmailResult;
  tokens: WidgetTokens;
}

export const EMAIL_RESULT_STYLES = `${CARD_PARTS_STYLES}
.gn-result-next { display:flex; flex-direction:column; gap:4px; }
.gn-result-next-label, .gn-result-next-text { padding:0 4px; }
.gn-result-next-text { font-size:13px; line-height:1.45; }
`;

function StatusIcon({ status }: { status: WidgetEmailStatus }) {
  switch (status) {
    case "accepted":
      return <PaperPlaneIcon size={18} />;
    case "bounced":
      return <BanIcon size={18} />;
    case "delivered":
      return <CheckIcon size={18} strokeWidth={2.6} />;
    case "failed":
      return <CircleXIcon size={18} />;
    case "sending":
      return <SpinnerIcon size={18} strokeWidth={2} />;
    case "unknown":
      return <CircleHelpIcon size={18} />;
  }
}

function statusTitle(status: WidgetEmailStatus): string {
  switch (status) {
    case "accepted":
      return m.widget_email_result_title_accepted();
    case "bounced":
      return m.widget_email_result_title_bounced();
    case "delivered":
      return m.widget_email_result_title_delivered();
    case "failed":
      return m.widget_email_result_title_failed();
    case "sending":
      return m.widget_email_result_title_sending();
    case "unknown":
      return m.widget_email_result_title_unknown();
  }
}

/** 「Next」下面那句话，有的状态会提到买家的邮箱。 */
function nextText(result: WidgetEmailResult): string {
  switch (result.status) {
    case "accepted":
    case "delivered":
      return m.widget_email_result_next_reply({ email: result.contactEmail });
    case "bounced":
      return m.widget_email_result_next_bounced();
    case "failed":
      return m.widget_email_result_next_failed();
    case "sending":
      return m.widget_email_result_next_sending();
    case "unknown":
      return m.widget_email_result_next_unknown();
  }
}

/** 出了问题的两个状态：图标圆换成危险色淡底。 */
function isProblem(status: WidgetEmailStatus): boolean {
  return status === "failed" || status === "bounced";
}

/**
 * 状态头图标圆的底：已接受和已送达是墨黑实心（同 Sent 卡的对勾）；发送中、状态未知是井色；
 * 出问题的是危险色淡底。失败态的墨黑答案留给 Retry 按钮。
 */
function statusTone(status: WidgetEmailStatus): WidgetIconTone {
  if (isProblem(status)) return "danger";
  return status === "sending" || status === "unknown" ? "soft" : "ink";
}

/** 卡底的按钮：发送中是禁用的说明，失败是重试，其余只在给了 onStartNew 时出；没有就是 null。 */
function actionButton({
  onRetry,
  onStartNew,
  status,
  tokens: t,
}: Pick<EmailResultCardProps, "onRetry" | "onStartNew" | "tokens"> & {
  status: WidgetEmailStatus;
}): VNode | null {
  if (status === "sending") {
    return (
      <FormButton disabled fullWidth tokens={t}>
        {m.widget_email_result_sending_action()}
      </FormButton>
    );
  }
  if (status === "failed" && onRetry) {
    return (
      <FormButton fullWidth onClick={onRetry} tokens={t}>
        <RefreshIcon size={14} />
        {m.widget_email_result_retry()}
      </FormButton>
    );
  }
  if (onStartNew !== undefined) {
    return (
      <FormButton fullWidth onClick={onStartNew} tokens={t} variant="secondary">
        {m.widget_email_result_start_new()}
      </FormButton>
    );
  }
  return null;
}

/**
 * 请求邮件的结果卡：一张卡按状态原地更新，不再另发消息。头部是图标圆加标题（状态变化时读屏礼貌播报），
 * 井里是请求编号、收件商家、买家邮箱、时间，再是「Next」一句话；退信时接着列出联系商家的其他办法。
 * 按钮：发送中是禁用的 Sending request，失败是墨黑的 Retry，给了 onStartNew 的是白底的 Start a new request。
 */
export function EmailResultCard({
  onRetry,
  onStartNew,
  onEdit,
  onCheck,
  result,
  tokens: t,
}: EmailResultCardProps) {
  const { status } = result;
  const waiting = status === "sending" || status === "accepted";
  const action = actionButton({ onRetry, onStartNew, status, tokens: t });
  return (
    <div
      aria-label={m.widget_email_result_group({ id: result.requestId })}
      class="gn-card"
      data-strong={isProblem(status) ? "" : undefined}
      role="group"
      style={formVars(t)}
    >
      <style>{`${FORM_STYLES}${CARD_STYLES}${EMAIL_RESULT_STYLES}`}</style>
      <StatusHeader
        icon={<StatusIcon status={status} />}
        live="status"
        subtitle={
          status === "accepted"
            ? m.widget_email_result_subtitle_accepted()
            : undefined
        }
        title={statusTitle(status)}
        tone={statusTone(status)}
      />
      <div class="gn-card-body">
        <div class="gn-well" data-roomy="">
          <Facts>
            {result.requestType && (
              <FactRow
                label={m.widget_contact_request_type()}
                value={REQUEST_TYPE_LABELS[requestTypes[result.requestType]]()}
              />
            )}
            <FactRow
              label={m.widget_email_result_request()}
              value={result.requestId}
            />
            <FactRow label={m.widget_email_result_to()} value={result.to} />
            <FactRow
              label={m.widget_email_result_email()}
              value={result.contactEmail}
            />
            {result.time && (
              <FactRow
                label={
                  waiting
                    ? m.widget_email_result_submitted()
                    : m.widget_email_result_updated()
                }
                value={result.time}
              />
            )}
            {/* 已接受时 Submitted 那行已经是这一刻；没有那行（Inbox 回放）就得单列受理时间。 */}
            {result.acceptedAt && (status !== "accepted" || !result.time) && (
              <FactRow
                label={m.widget_email_result_accepted_fact()}
                value={result.acceptedAt}
              />
            )}
          </Facts>
        </div>
        <div class="gn-result-next">
          <div class="gn-caption gn-result-next-label">
            {m.widget_email_result_next()}
          </div>
          <div class="gn-result-next-text">{nextText(result)}</div>
          {hasStoreContact(result.contact) && (
            <StoreContactList contact={result.contact} />
          )}
        </div>
      </div>
      {(action !== null || onEdit || onCheck) && (
        <div class="gn-card-foot">
          {action}
          {onEdit && (
            <FormButton
              tokens={t}
              variant="secondary"
              fullWidth
              onClick={onEdit}
            >
              {m.widget_email_result_edit()}
            </FormButton>
          )}
          {onCheck && (
            <FormButton
              tokens={t}
              variant="secondary"
              fullWidth
              onClick={onCheck}
            >
              {m.widget_email_result_check()}
            </FormButton>
          )}
        </div>
      )}
    </div>
  );
}
